import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, MapPin } from 'lucide-react';
import { DraggablePanel } from '../common/DraggablePanel';
import { GOOGLE_API_KEY } from '../../config/google';

interface StreetViewPanelProps {
  onClose: () => void;
  selectedPoint: [number, number] | null; // [lon, lat]
}

export const StreetViewPanel: React.FC<StreetViewPanelProps> = ({ onClose, selectedPoint }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { t, i18n } = useTranslation();

  const embedUrl = selectedPoint
    ? `https://www.google.com/maps/embed/v1/streetview?key=${GOOGLE_API_KEY}&location=${selectedPoint[1]},${selectedPoint[0]}&heading=0&pitch=0&fov=90&language=${i18n.language}`
    : null;

  const mapsLink = selectedPoint
    ? `https://www.google.com/maps/@${selectedPoint[1]},${selectedPoint[0]},17z/data=!3m1!1e3`
    : null;

  return (
    <DraggablePanel
      title={t('tools.street_view')}
      onClose={onClose}
      icon={<Eye size={14} />}
      width={340}
    >
      <div className="p-3 space-y-3">
        {!selectedPoint ? (
          <div className="text-[11px] text-gn-warning bg-gn-warning-light border border-gn rounded px-2 py-1.5 flex items-center gap-1.5">
            <MapPin size={12} />
            {t('streetview.click')}
          </div>
        ) : (
          <>
            <div className="text-[11px] text-gn-success bg-gn-success-light border border-gn rounded px-2 py-1.5">
              {t('streetview.location', { lat: selectedPoint[1].toFixed(5), lon: selectedPoint[0].toFixed(5) })}
            </div>

            {embedUrl ? (
              <div className="w-full h-48 rounded overflow-hidden border border-gn">
                <iframe
                  ref={iframeRef}
                  src={embedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="w-full h-48 rounded bg-gn-secondary flex items-center justify-center text-xs text-gn-secondary">
                {t('streetview.unavailable')}
              </div>
            )}

            {mapsLink && (
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-[11px] text-gn-blue hover:text-gn-blue underline"
              >
                {t('streetview.open_google')}
              </a>
            )}
          </>
        )}
      </div>
    </DraggablePanel>
  );
};
